import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Mail, Phone, MapPin, Send, Clock, CheckCircle } from "lucide-react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import toast from "react-hot-toast";

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  type: "inquiry" | "suggestion" | "bug" | "other";
}

/**
 * ContactPage Component
 * Provides contact form and company information for inquiries and suggestions
 * Features: Contact form, company info, FAQ, and multiple contact methods
 */
const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log("Contact form data:", data);
    toast.success("문의가 성공적으로 전송되었습니다!");
    reset();
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              문의 및 제안
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              궁금한 점이 있으시거나 개선 제안이 있으시면 언제든지 연락해주세요. 
              여러분의 의견은 ReXume을 더 나은 서비스로 만드는 데 큰 도움이 됩니다.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  문의하기
                </h2>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        이름 <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("name", { required: "이름을 입력해주세요" })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="홍길동"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        이메일 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        {...register("email", { 
                          required: "이메일을 입력해주세요",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "올바른 이메일 형식을 입력해주세요"
                          }
                        })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="hong@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      문의 유형 <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...register("type", { required: "문의 유형을 선택해주세요" })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">선택해주세요</option>
                      <option value="inquiry">일반 문의</option>
                      <option value="suggestion">개선 제안</option>
                      <option value="bug">버그 신고</option>
                      <option value="other">기타</option>
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      제목 <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register("subject", { required: "제목을 입력해주세요" })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="문의 제목을 입력해주세요"
                    />
                    {errors.subject && (
                      <p className="text-red-500 text-sm mt-1">{errors.subject.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내용 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...register("message", { 
                        required: "내용을 입력해주세요",
                        minLength: {
                          value: 10,
                          message: "최소 10자 이상 입력해주세요"
                        }
                      })}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="문의하실 내용을 자세히 입력해주세요..."
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        전송 중...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        문의 전송
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info & FAQ */}
            <div className="space-y-8">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  연락처 정보
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">이메일</p>
                      <a
                        href="mailto:contact@rexume.com"
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        contact@rexume.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">전화</p>
                      <a
                        href="tel:+82-2-1234-5678"
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        +82-2-1234-5678
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">주소</p>
                      <p className="text-gray-600">
                        서울특별시 강남구<br />
                        테헤란로 123, 456호
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  운영 시간
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-900">평일</p>
                      <p className="text-gray-600 text-sm">09:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-900">주말</p>
                      <p className="text-gray-600 text-sm">휴무</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  자주 묻는 질문
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">
                      문의 응답은 언제 받을 수 있나요?
                    </p>
                    <p className="text-gray-600 text-sm">
                      평일 기준 24시간 이내에 답변드립니다.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">
                      제안사항은 어떻게 검토되나요?
                    </p>
                    <p className="text-gray-600 text-sm">
                      모든 제안사항은 팀에서 검토 후 피드백을 드립니다.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">
                      긴급한 문의는 어떻게 하나요?
                    </p>
                    <p className="text-gray-600 text-sm">
                      전화로 연락주시면 빠른 처리 가능합니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900 text-sm">
                      문의가 접수되었습니다
                    </p>
                    <p className="text-green-700 text-xs">
                      빠른 시일 내에 답변드리겠습니다
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
